import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";


const PARAMETERS_SELECTIONS_KEY = "parametersSelections";

type CompanyMetadataRecord = {
  company_id: number | string;
  industry: string;
  sector: string;
  geography: string;
  company_size_bucket: string;
  ownership_type: string;
};

type OptionItem = {
  id: number;
  name: string;
};

const getInitialSelections = () => {
  try {
    const raw = localStorage.getItem(PARAMETERS_SELECTIONS_KEY);
    if (!raw) {
      return {
        selectedIndustry: [],
        selectedSector: [],
        selectedCompanySizes: [],
        selectedOwnershipType: [],
        selectedGeography: [],
      };
    }

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return {
        selectedIndustry: [],
        selectedSector: [],
        selectedCompanySizes: [],
        selectedOwnershipType: [],
        selectedGeography: [],
      };
    }

    const toStringArray = (value) => {
      if (Array.isArray(value)) {
        return value.filter((item) => typeof item === "string");
      }

      if (typeof value === "string" && value.trim()) {
        return [value];
      }

      return [];
    };

    return {
      selectedIndustry: toStringArray(parsed.selectedIndustry),
      selectedSector: toStringArray(parsed.selectedSector),
      selectedCompanySizes: Array.isArray(parsed.selectedCompanySizes)
        ? parsed.selectedCompanySizes.filter((value) => typeof value === "string")
        : [],
      selectedOwnershipType: toStringArray(parsed.selectedOwnershipType),
      selectedGeography: toStringArray(parsed.selectedGeography),
    };
  } catch {
    return {
      selectedIndustry: [],
      selectedSector: [],
      selectedCompanySizes: [],
      selectedOwnershipType: [],
      selectedGeography: [],
    };
  }
};

const buildOptions = (values: Array<string | null | undefined>): OptionItem[] =>
  [...new Set(values.filter((value): value is string => typeof value === "string" && value.trim().length > 0))].map((value, index) => ({
    id: index + 1,
    name: value,
  }));

const getOptionNames = (options: OptionItem[]) => options.map((option) => option.name);

const areAllSelected = (selectedValues: string[], options: OptionItem[]) => {
  if (options.length === 0) {
    return false;
  }

  const optionNames = getOptionNames(options);
  return optionNames.every((name) => selectedValues.includes(name));
};

function Parameters({
  compareCompanyView,
  onContinue,
  onSelectionChange,
  hideContinueButton = false,
}) {

  const selectedAccounts = (() => {
    try {
      const raw = localStorage.getItem("selectedAccountAlignedList");
      if (!raw) return [];

      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];

      return parsed.filter((item) => typeof item === "string" && item.trim());
    } catch {
      return [];
    }
  })();

  console.log("Parameters component loaded with accounts:", selectedAccounts);
  const API_HOST = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  const COMPANY_METADATA_URL =
    selectedAccounts.length > 0
      ? `${API_HOST}/api/company-metadata/distinct?account_aligned=${encodeURIComponent(selectedAccounts.join(","))}`
      : `${API_HOST}/api/company-metadata/distinct`;
  const initialSelections = getInitialSelections();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [selectedIndustry, setSelectedIndustry] = useState(
    initialSelections.selectedIndustry,
  );
  const [selectedSector, setSelectedSector] = useState(
    initialSelections.selectedSector,
  );
  const [selectedCompanySizes, setSelectedCompanySizes] = useState(
    initialSelections.selectedCompanySizes,
  );
  const [selectedOwnershipType, setSelectedOwnershipType] = useState(
    initialSelections.selectedOwnershipType,
  );
  const [selectedGeography, setSelectedGeography] = useState(
    initialSelections.selectedGeography,
  );
  const [data, setData] = useState<CompanyMetadataRecord[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(
        PARAMETERS_SELECTIONS_KEY,
        JSON.stringify({
          selectedIndustry,
          selectedSector,
          selectedCompanySizes,
          selectedOwnershipType,
          selectedGeography,
        }),
      );
    } catch {
      // Ignore storage errors.
    }
  }, [
    selectedIndustry,
    selectedSector,
    selectedCompanySizes,
    selectedOwnershipType,
    selectedGeography,
  ]);

  useEffect(() => {
    let isMounted = true;

    const fetchCompanyMetadata = async () => {
      setLoading(true);

      try {
        const response = await axios.get(COMPANY_METADATA_URL);

        if (isMounted) {
          setData(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error fetching data:", error);
          setData([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCompanyMetadata();

    return () => {
      isMounted = false;
    };
  }, []);

  const industryMatches = selectedIndustry.length > 0
    ? data.filter((company) => selectedIndustry.includes(company.industry))
    : [];
  const sectorMatches = selectedSector.length > 0
    ? industryMatches.filter((company) => selectedSector.includes(company.sector))
    : [];

  const uniqueIndustries = buildOptions(data.map((company) => company.industry));
  const filteredSectors = buildOptions(
    industryMatches.map((company) => company.sector),
  );
  const geographies = buildOptions(
    industryMatches.map((company) => company.geography),
  );
  const companySizes = buildOptions(
    industryMatches.map((company) => company.company_size_bucket),
  );
  const ownershipTypes = buildOptions(
    industryMatches.map((company) => company.ownership_type),
  );

  // Base set for company IDs: start from industry matches, narrow down by each
  // subsequent filter only when that filter has a value.
  const baseForIds = selectedSector.length > 0 ? sectorMatches : industryMatches;
  const filteredCompanyIds = baseForIds
    .filter(
      (company) =>
        (selectedCompanySizes.length === 0 ||
          selectedCompanySizes.includes(company.company_size_bucket)) &&
        (selectedGeography.length === 0 || selectedGeography.includes(company.geography)) &&
        (selectedOwnershipType.length === 0 || selectedOwnershipType.includes(company.ownership_type)),
    )
    .map((company) => company.company_id);
  console.log("Filtered Company IDs:", filteredCompanyIds);

  const canContinue =
    selectedIndustry.length > 0 &&
    selectedSector.length > 0 &&
    selectedCompanySizes.length > 0 &&
    selectedGeography.length > 0 &&
    selectedOwnershipType.length > 0;
  const completedSelectionsCount = [
    selectedIndustry.length > 0,
    selectedSector.length > 0,
    selectedCompanySizes.length > 0,
    selectedGeography.length > 0,
    selectedOwnershipType.length > 0,
  ].filter(Boolean).length;
  const stripWidthPercent = Math.max(18, (completedSelectionsCount / 5) * 100);

  useEffect(() => {
    try {
      localStorage.setItem("filteredCompanyIds", JSON.stringify(filteredCompanyIds));
      window.dispatchEvent(new Event("filteredCompanyIdsUpdated"));
    } catch {
      // Ignore storage errors.
    }
  }, [filteredCompanyIds]);

  useEffect(() => {
    if (typeof onSelectionChange === "function") {
      onSelectionChange({
        isComplete: selectedIndustry.length > 0,
        selectionSignature: JSON.stringify({
          selectedIndustry,
          selectedSector,
          selectedCompanySizes,
          selectedOwnershipType,
          selectedGeography,
          filteredCompanyIds,
        }),
      });
    }
  }, [
    canContinue,
    selectedIndustry,
    selectedSector,
    selectedCompanySizes,
    selectedOwnershipType,
    selectedGeography,
    filteredCompanyIds,
    onSelectionChange,
  ]);

  const handleIndustryChange = (industry) => {
    setSelectedIndustry((previous) =>
      previous.includes(industry)
        ? previous.filter((value) => value !== industry)
        : [...previous, industry],
    );
    setSelectedSector([]);
    setSelectedCompanySizes([]);
    setSelectedGeography([]);
    setSelectedOwnershipType([]);
  };

  const handleIndustrySelectAll = () => {
    const nextIndustries = areAllSelected(selectedIndustry, uniqueIndustries)
      ? []
      : getOptionNames(uniqueIndustries);

    setSelectedIndustry(nextIndustries);
    setSelectedSector([]);
    setSelectedCompanySizes([]);
    setSelectedGeography([]);
    setSelectedOwnershipType([]);
  };

  const handleSectorChange = (sector) => {
    setSelectedSector((previous) =>
      previous.includes(sector)
        ? previous.filter((value) => value !== sector)
        : [...previous, sector],
    );
  };

  const handleSectorSelectAll = () => {
    const nextSectors = areAllSelected(selectedSector, filteredSectors)
      ? []
      : getOptionNames(filteredSectors);

    setSelectedSector(nextSectors);
  };

  const handleGeographyChange = (geography) => {
    setSelectedGeography((previous) =>
      previous.includes(geography)
        ? previous.filter((value) => value !== geography)
        : [...previous, geography],
    );
  };

  const handleGeographySelectAll = () => {
    const nextGeographies = areAllSelected(selectedGeography, geographies)
      ? []
      : getOptionNames(geographies);

    setSelectedGeography(nextGeographies);
  };

  const handleOwnershipTypeChange = (ownershipType) => {
    setSelectedOwnershipType((previous) =>
      previous.includes(ownershipType)
        ? previous.filter((value) => value !== ownershipType)
        : [...previous, ownershipType],
    );
  };

  const handleOwnershipTypeSelectAll = () => {
    const nextOwnershipTypes = areAllSelected(selectedOwnershipType, ownershipTypes)
      ? []
      : getOptionNames(ownershipTypes);

    setSelectedOwnershipType(nextOwnershipTypes);
  };

  const handleCompanySizeToggle = (companySize) => {
    setSelectedCompanySizes((previous) =>
      previous.includes(companySize)
        ? previous.filter((size) => size !== companySize)
        : [...previous, companySize],
    );
  };

  const handleCompanySizeSelectAll = () => {
    const nextCompanySizes = areAllSelected(selectedCompanySizes, companySizes)
      ? []
      : getOptionNames(companySizes);

    setSelectedCompanySizes(nextCompanySizes);
  };

  const handleContinue = () => {
    localStorage.setItem(
      "filteredCompanyIds",
      JSON.stringify(filteredCompanyIds),
    );
    window.dispatchEvent(new Event("filteredCompanyIdsUpdated"));
    if (typeof onContinue === "function") {
      onContinue();
      return;
    }
    navigate("/kpi-selector");
  };

  return (
    <div
      className={`${
        isDarkMode ? "bg-black" : "bg-[#f4fbf6]"
      } w-full relative overflow-visible`}
    >
      {!isDarkMode && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-16 -left-10 h-56 w-56 rounded-full bg-emerald-100/70 blur-3xl"></div>
          <div className="absolute top-20 right-10 h-48 w-48 rounded-full bg-sky-100/70 blur-3xl"></div>
          <div className="absolute bottom-8 left-1/3 h-44 w-44 rounded-full bg-amber-100/60 blur-3xl"></div>
        </div>
      )}

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-2 pb-0 overflow-visible">
        {/* <div className="max-w-7xl mx-auto px-6 pt-4">
          <WorkflowStatusBar activeSteps={canContinue ? 1 : 0} />
        </div>

        <div className="text-center pt-3 pb-4">
          <span className="inline-flex items-center rounded-full border border-emerald-300/80 bg-emerald-50 px-4 py-1 text-xs font-bold uppercase tracking-wide text-emerald-800 dark:border-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
            Benchmark Setup: Step 1 of 3
          </span>
          <h2 className="mt-3 text-2xl md:text-3xl font-extrabold text-gray-900 dark:text-white">
            Please provide the parameters for comparable insights
          </h2>
          <p className="mt-2 text-sm md:text-base text-gray-600 dark:text-gray-300">
            Select the parameters to generate KPI outputs.
          </p>
        </div> */}

        {loading && (
          <div className="flex flex-col items-center justify-center py-12">
            <div
              className="h-12 w-12 animate-spin rounded-full border-4 border-gray-300 dark:border-gray-700 border-t-green-700"
              aria-label="Loading"
            ></div>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
              Loading data...
            </p>
          </div>
        )}
        {!loading && (
        <div className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-[0_10px_26px_rgba(16,24,40,0.08)] p-5 md:p-6 mb-8">
          {/* <div className="w-full h-1.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 mb-5 overflow-hidden">
            <div
              className="h-full rounded-full bg-emerald-500 transition-all duration-500 ease-out"
              style={{ width: `${stripWidthPercent}%` }}
            ></div>
          </div> */}
          <div className="mb-3 flex items-center justify-between gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
            <div className="flex items-center gap-2">
              <span className="inline-block h-4 w-[3px] rounded-full bg-lime-500"></span>
              <span>Parameter Selection</span>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
              <div className="mb-3 flex items-center justify-between gap-2 text-[15px] font-semibold text-gray-800 dark:text-gray-100">
                <div className="flex items-center gap-2">
                  <span className="inline-block h-4 w-[3px] rounded-full bg-lime-500"></span>
                  <span>Industry</span>
                </div>
                <label className="inline-flex items-center gap-2 text-xs font-semibold text-gray-600 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={areAllSelected(selectedIndustry, uniqueIndustries)}
                    disabled={uniqueIndustries.length === 0}
                    onChange={handleIndustrySelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-lime-600 focus:ring-lime-500"
                  />
                  Select All
                </label>
              </div>
              <div className="max-h-44 overflow-y-auto pr-1">
                <div className="flex flex-wrap gap-2">
                  {uniqueIndustries.map((industryOption) => {
                    const isSelected = selectedIndustry.includes(industryOption.name);
                    return (
                      <button
                        key={industryOption.id}
                        type="button"
                        onClick={() => handleIndustryChange(industryOption.name)}
                        className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                          isSelected
                            ? "border-lime-500 bg-lime-500 text-white"
                            : "border-gray-200 bg-gray-50 text-gray-700 hover:border-lime-400 hover:bg-lime-50 hover:text-lime-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-lime-500 dark:hover:bg-lime-950/40"
                        }`}
                      >
                        {industryOption.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
              <div className="mb-3 flex items-center justify-between gap-2 text-[15px] font-semibold text-gray-800 dark:text-gray-100">
                <div className="flex items-center gap-2">
                  <span className="inline-block h-4 w-[3px] rounded-full bg-lime-500"></span>
                  <span>Sector</span>
                </div>
                <label className="inline-flex items-center gap-2 text-xs font-semibold text-gray-600 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={areAllSelected(selectedSector, filteredSectors)}
                    disabled={selectedIndustry.length === 0 || filteredSectors.length === 0}
                    onChange={handleSectorSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-lime-600 focus:ring-lime-500"
                  />
                  Select All
                </label>
              </div>
              {selectedIndustry.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Select industry first.</p>
              ) : (
                <div className="max-h-44 overflow-y-auto pr-1">
                  <div className="flex flex-wrap gap-2">
                    {filteredSectors.map((sectorOption) => {
                      const isSelected = selectedSector.includes(sectorOption.name);
                      return (
                        <button
                          key={sectorOption.id}
                          type="button"
                          onClick={() => handleSectorChange(sectorOption.name)}
                          className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                            isSelected
                              ? "border-lime-500 bg-lime-500 text-white"
                              : "border-gray-200 bg-gray-50 text-gray-700 hover:border-lime-400 hover:bg-lime-50 hover:text-lime-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-lime-500 dark:hover:bg-lime-950/40"
                          }`}
                        >
                          {sectorOption.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
              <div className="mb-3 flex items-center justify-between gap-2 text-[15px] font-semibold text-gray-800 dark:text-gray-100">
                <div className="flex items-center gap-2">
                  <span className="inline-block h-4 w-[3px] rounded-full bg-lime-500"></span>
                  <span>Company Size ({selectedCompanySizes.length} selected)</span>
                </div>
                <label className="inline-flex items-center gap-2 text-xs font-semibold text-gray-600 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={areAllSelected(selectedCompanySizes, companySizes)}
                    disabled={selectedIndustry.length === 0 || companySizes.length === 0}
                    onChange={handleCompanySizeSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-lime-600 focus:ring-lime-500"
                  />
                  Select All
                </label>
              </div>
              {selectedIndustry.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Select industry first.</p>
              ) : (
                <div className="max-h-44 overflow-y-auto pr-1">
                  <div className="flex flex-wrap gap-2">
                    {companySizes.map((companySizeOption) => {
                      const isSelected = selectedCompanySizes.includes(companySizeOption.name);
                      return (
                        <button
                          key={companySizeOption.id}
                          type="button"
                          onClick={() => handleCompanySizeToggle(companySizeOption.name)}
                          className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                            isSelected
                              ? "border-lime-500 bg-lime-500 text-white"
                              : "border-gray-200 bg-gray-50 text-gray-700 hover:border-lime-400 hover:bg-lime-50 hover:text-lime-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-lime-500 dark:hover:bg-lime-950/40"
                          }`}
                        >
                          {companySizeOption.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
              <div className="mb-3 flex items-center justify-between gap-2 text-[15px] font-semibold text-gray-800 dark:text-gray-100">
                <div className="flex items-center gap-2">
                  <span className="inline-block h-4 w-[3px] rounded-full bg-lime-500"></span>
                  <span>Geography</span>
                </div>
                <label className="inline-flex items-center gap-2 text-xs font-semibold text-gray-600 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={areAllSelected(selectedGeography, geographies)}
                    disabled={selectedIndustry.length === 0 || geographies.length === 0}
                    onChange={handleGeographySelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-lime-600 focus:ring-lime-500"
                  />
                  Select All
                </label>
              </div>
              {selectedIndustry.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Select industry first.</p>
              ) : (
                <div className="max-h-44 overflow-y-auto pr-1">
                  <div className="flex flex-wrap gap-2">
                    {geographies.map((geographyOption) => {
                      const isSelected = selectedGeography.includes(geographyOption.name);
                      return (
                        <button
                          key={geographyOption.id}
                          type="button"
                          onClick={() => handleGeographyChange(geographyOption.name)}
                          className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                            isSelected
                              ? "border-lime-500 bg-lime-500 text-white"
                              : "border-gray-200 bg-gray-50 text-gray-700 hover:border-lime-400 hover:bg-lime-50 hover:text-lime-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-lime-500 dark:hover:bg-lime-950/40"
                          }`}
                        >
                          {geographyOption.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900 lg:col-span-2">
              <div className="mb-3 flex items-center justify-between gap-2 text-[15px] font-semibold text-gray-800 dark:text-gray-100">
                <div className="flex items-center gap-2">
                  <span className="inline-block h-4 w-[3px] rounded-full bg-lime-500"></span>
                  <span>Ownership Type</span>
                </div>
                <label className="inline-flex items-center gap-2 text-xs font-semibold text-gray-600 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={areAllSelected(selectedOwnershipType, ownershipTypes)}
                    disabled={selectedIndustry.length === 0 || ownershipTypes.length === 0}
                    onChange={handleOwnershipTypeSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-lime-600 focus:ring-lime-500"
                  />
                  Select All
                </label>
              </div>
              {selectedIndustry.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Select industry first.</p>
              ) : (
                <div className="max-h-44 overflow-y-auto pr-1">
                  <div className="flex flex-wrap gap-2">
                    {ownershipTypes.map((ownershipTypeOption) => {
                      const isSelected = selectedOwnershipType.includes(ownershipTypeOption.name);
                      return (
                        <button
                          key={ownershipTypeOption.id}
                          type="button"
                          onClick={() => handleOwnershipTypeChange(ownershipTypeOption.name)}
                          className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                            isSelected
                              ? "border-lime-500 bg-lime-500 text-white"
                              : "border-gray-200 bg-gray-50 text-gray-700 hover:border-lime-400 hover:bg-lime-50 hover:text-lime-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-lime-500 dark:hover:bg-lime-950/40"
                          }`}
                        >
                          {ownershipTypeOption.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        )}


        {/* {(compareCompanyView || canContinue) && (
          <div className="flex justify-center pt-6">
            <div className="w-full max-w-6xl grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
              {compareCompanyView && (
                <div
                  tabIndex={0}
                  className="bg-sky-50 dark:bg-sky-950/30 p-5 rounded-2xl border border-sky-200 dark:border-sky-800 transition-all duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 text-center w-full md:max-w-md md:col-start-1 md:justify-self-start shadow-sm"
                >
                  <h2 className="text-xl font-bold text-sky-900 dark:text-sky-200 mt-0 mb-2">
                    Compare to Company
                  </h2>
                  <p className="text-sm text-sky-800/80 dark:text-sky-100/80 leading-relaxed">
                    File name: {uploadedTempFileName}
                  </p>
                </div>
              )}

              {canContinue && (
                <div
                  tabIndex={0}
                  className={`bg-white dark:bg-gray-950 p-5 rounded-2xl border-2 border-emerald-500 transition-all duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600 text-center w-full max-w-xl shadow-[0_10px_24px_rgba(16,185,129,0.18)] ${compareCompanyView ? "md:col-start-3 md:justify-self-end" : "md:col-start-2 md:justify-self-center"}`}
                >
                  <h2 className="text-xl font-bold text-emerald-900 dark:text-emerald-200 mt-0 mb-2">
                    Dataset Preview
                  </h2>
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                    Company Size: {selectedCompanySizes.join(", ") || "-"}
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                    Comparable Companies: {filteredCompanyIds.length}
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                    Industry: {selectedIndustry}
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                    Geography: {selectedGeography}
                  </p>
                </div>
              )}
            </div>
          </div>
        )} */}

        {canContinue && !hideContinueButton && (
          <div className="flex justify-center pt-6">
            <button
              onClick={handleContinue}
              className="inline-flex items-center gap-2 px-7 py-2.5 rounded-xl font-semibold tracking-wide transition-all bg-emerald-700 text-white hover:bg-emerald-800 hover:shadow-lg hover:shadow-emerald-300/40 dark:hover:shadow-none"
            >
              Next: Select KPIs
              <span aria-hidden="true">-&gt;</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Parameters;
